import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import { SectionBlock } from '../../../blocks/SectionBlock';
import { Table } from '../../../common/Table';
import { useAuth } from '../../../contexts/AuthContext';
import { useGetAllTeams } from '../../../hooks/useGetAllTeams';
import { TeamBlockers } from '../../../services/dashboardBlockers';
import { TeamIdentity } from '../../../types';
import { Blockers } from '../../YourTeams/components/SprintSummary/Blockers';
import { BlockerItem } from '../../YourTeams/components/SprintSummary/types';
import { useTeamBlockers } from '../hooks/useTeamBlockers';

export function BlockersSection(): JSX.Element {
  const { userData } = useAuth();
  const { data: allTeams = [] } = useGetAllTeams(userData?.role === 'partner');
  const {
    data: teamBlockers = [],
    isLoading,
    error,
  } = useTeamBlockers(
    userData?.role === 'partner' ? allTeams : userData?.accessibleTeams ?? [],
  );

  const headerContent = (
    <div className="flex items-center gap-2">
      <NoSymbolIcon className="w-5 h-5 text-red-500" />
      <h2 className="text-xl font-semibold">Blockers in Your Teams</h2>
    </div>
  );

  return (
    <SectionBlock skipContentContainer headerContent={headerContent}>
      <Table
        borderStyle="dashed"
        className="bg-white my-4"
        columns={[
          {
            key: 'team',
            width: '25%',
            title: (
              <div className="h-full justify-center flex items-center">
                Team
              </div>
            ),
            borders: {
              right: true,
            },
            verticalAlign: 'top',
            render: (data: TeamIdentity & { blockers: BlockerItem[] }) => {
              return (
                <div className="flex mx-2  gap-2">
                  <Link
                    to={`/your-teams/${data.id}/teamView`}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {data.name}
                  </Link>
                </div>
              );
            },
          },
          {
            key: 'blockers',
            title: (
              <div className="h-full mx-4 flex items-center">Blockers</div>
            ),
            render: (data: TeamIdentity & { blockers: BlockerItem[] }) => {
              return (
                <div className="flex flex-col gap-2">
                  {data.blockers.map((blocker: BlockerItem) => (
                    <Blockers
                      skipHeader
                      skipAddedBy
                      key={blocker.id}
                      blockers={[blocker]}
                      isLoading={false}
                      error={null}
                    />
                  ))}
                </div>
              );
            },
          },
        ]}
        data={teamBlockers.map((teamBlocker: TeamBlockers) => ({
          id: teamBlocker.team.id,
          name: teamBlocker.team.name,
          blockers: teamBlocker.blockers,
        }))}
        emptyStateMessage="No blockers detected."
        isLoading={isLoading}
        error={error}
      />
    </SectionBlock>
  );
}
